// ==========================================================================
// Plyr source update
// ==========================================================================

import { providers } from './config/types';
import html5 from './html5';
import media from './media';
import PreviewThumbnails from './plugins/preview-thumbnails';
import support from './support';
import ui from './ui';
import { createElement, insertElement, removeElement } from './utils/elements';
import is from './utils/is';
import { getDeep } from './utils/objects';
import starplayer from './plugins/starplayer';
import Fullscreen from './fullscreen';
import Tracker from './plugins/tracker';
import browser from './utils/browser';
import activex from './plugins/activex';
import hls from './plugins/hls';
import dash from './plugins/dash';
import defaults from './config/defaults';
import { ready } from './utils/events';

const source = {
  // Add elements to HTML5 media (source, tracks, etc)
  insertElements(type, attributes) {
    if (is.string(attributes)) {
      insertElement(type, this.media, {
        src: attributes,
      });
    } else if (is.array(attributes)) {
      attributes.forEach((attribute) => {
        insertElement(type, this.media, attribute);
      });
    }
  },

  // Update source
  // Sources are not checked for support so be careful
  change(input) {
    if (!getDeep(input, 'sources.length')) {
      this.debug.warn('Invalid source format');
      return;
    }

    ready.call(this).then(() => {

      // Cancel current network requests

      if (this.isHTML5) {
        html5.cancelRequests.call(this);
      } else if (this.isStarPlayer) {
        if (browser.isIE) {
          activex.destroy.call(this);
        } else {
          starplayer.cancelRequests.call(this);
        }
      }

      const media_setup = (input, sources) => {
        // Set up from scratch
        // media.setup.call(this);
        media.setup
          .call(this)
          .then(() => {

            // HTML5 stuff
            if (this.isHTML5_or_StarPlayer) {
              // Setup captions
              if (Object.keys(input).includes('tracks')) {
                source.insertElements.call(this, 'track', input.tracks);
              }
            }

            if (this.isStarPlayer && (browser.isIos || browser.isAndroid)) {
              return;
            }

            if (this.isActivex) {
              if (!is.empty(input.previewThumbnails)) {
                Object.assign(this.config.previewThumbnails, input.previewThumbnails);

                // Cleanup previewThumbnails plugin if it was loaded
                if (this.previewThumbnails && this.previewThumbnails.loaded) {
                  this.previewThumbnails.destroy();
                  this.previewThumbnails = null;
                }
              }
              return;
            }

            // Setup fullscreen
            this.fullscreen = new Fullscreen(this);

            // If HTML5 or embed but not fully supported, setupInterface and call ready now
            if (this.isHTML5_or_StarPlayer || (this.isEmbed && !this.supported.ui)) {
              // Setup interface
              ui.build.call(this);
            }

            // Container listeners
            this.listeners.container();

            // Global listeners
            this.listeners.global();

            // Tracker
            if (this.isHTML5_or_StarPlayer) {
              this.tracker = new Tracker(this);
            }

            // Cleanup previewThumbnails plugin if it was loaded
            if (this.previewThumbnails && this.previewThumbnails.loaded) {
              this.previewThumbnails.destroy();
              this.previewThumbnails = null;
            }

            // Update previewThumbnails config & reload plugin
            if (!is.empty(input.previewThumbnails)) {
              Object.assign(this.config.previewThumbnails, input.previewThumbnails);

              // Create new instance if it is still enabled
              if (this.config.previewThumbnails.enabled) {
                this.previewThumbnails = new PreviewThumbnails(this);
              }
            }

            // Load HTML5 sources
            if (this.isHTML5_or_StarPlayer && !hls.ishls.call(this, sources) && !dash.isdash.call(this, sources)) {
              this.media.load();
            }
            
            // Update the fullscreen support
            this.fullscreen.update();
          })
          .catch(() => {});
      }

      if (this.stars.connectionTimer) {
        clearTimeout(this.stars.connectionTimer);
      }
      this.stars.connectionTimer = setTimeout(()=>{
        // Destroy instance and re-setup
        this.destroy.call(
          this,
          () => {
            // Reset quality options
            this.options.quality = [];

            // Remove elements
            removeElement(this.media);
            this.media = null;

            // Reset class name
            if (is.element(this.elements.container)) {
              this.elements.container.removeAttribute('class');
            }

            // Set the type and provider
            const { sources, type } = input;
            const [{ provider = providers.html5, src }] = sources;
            const tagName = provider === 'html5' || provider === 'starplayer' ? type : 'div';
            const attributes = provider === 'html5' || provider === 'starplayer' ? {} : { src };

            Object.assign(this, {
              provider,
              type,
              // Check for support
              supported: support.check(type, provider, this.config.playsinline),
              // Create new element
              media: createElement(tagName, attributes),
            });

            // Inject the new element
            this.elements.container.appendChild(this.media);

            // Autoplay the new source?
            if (is.boolean(input.autoplay)) {
              this.config.autoplay = input.autoplay;
            }

            // Set attributes for audio and video
            if (this.isHTML5_or_StarPlayer) {
              if (this.config.crossorigin) {
                this.media.setAttribute('crossorigin', '');
              }
              if (this.config.autoplay) {
                this.media.setAttribute('autoplay', '');
              }
              if (!is.empty(input.poster)) {
                this.poster = input.poster;
              }
              if (this.config.loop.active) {
                this.media.setAttribute('loop', '');
              }
              if (this.config.muted) {
                this.media.setAttribute('muted', '');
              }
              if (this.config.playsinline) {
                this.media.setAttribute('playsinline', '');
              }
            }

            // Restore class hook
            ui.addStyleHook.call(this);

            if (this.isHTML5) {
              source.insertElements.call(this, 'source', sources);
            }

            if (this.isStarPlayer) {
              source.insertElements.call(this, 'sourcex', sources);
            }

            // Set video title
            this.config.title = input.title;

            if (this.hls) {
              this.hls.stopLoad();
              this.hls.destroy();
              this.hls = null;
            }
            
            if (this.dash) {
              this.dash.destroy();
              this.dash = null;
            }

            this.config.quality = this.config.defaultQuality;

            if (hls.ishls.call(this, sources)) {
              if (this.isStarPlayer) {
                media.initElement.call(this);
                starplayer.getNewHostUrl.call(this, sources).then((url)=> {
                  hls.setup.call(this, url).then((quality)=> {
                    this.config.quality = quality;
                    media_setup.call(this, input, sources);
                  }).catch(() => {
                    media_setup.call(this, input, sources);
                  });
                }).catch(()=>{
                });
              } else {
                hls.setup.call(this, sources).then((quality)=> {
                  this.config.quality = quality;
                  media_setup.call(this, input, sources);
                }).catch(() => {
                  media_setup.call(this, input, sources);
                });
              }
            } else if (dash.isdash.call(this, sources)) {
              dash.setup.call(this, sources).then((quality)=> {
                this.config.quality = quality;
                media_setup.call(this, input, sources);
              }).catch(() => {
                media_setup.call(this, input, sources);
              });
            } else {
              media_setup.call(this, input, sources);
            }

          },
          true,
        );
      }, 500);
    });
    
  },
};

export default source;
